import { useState } from "react";
import "../../styles/apply-form.css";

const API_BASE_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_ADMIN_API_URL || "https://ampsweb.saassrs.com";

export default function ApplyWithoutRegistration({ job, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    email: "",
    firstname: "",
    lastname: "",
    mobile_number: "",
    country: "",
    state: "",
    city: "",
    address: "",
    totalexp: "",
    relavantexp: "",
    custom_fields: {},
    documents: []
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.startsWith('custom_fields.')) {
      const fieldName = name.replace('custom_fields.', '');
      setFormData(prev => ({
        ...prev,
        custom_fields: {
          ...prev.custom_fields,
          [fieldName]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Handle file uploads
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setFormData(prev => ({
      ...prev,
      documents: [...prev.documents, ...files]
    }));
  };

  // Remove a document
  const removeDocument = (index) => {
    setFormData(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.email || !formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.firstname || !formData.firstname.trim()) {
      newErrors.firstname = "First name is required";
    }

    if (!formData.mobile_number || !formData.mobile_number.trim()) {
      newErrors.mobile_number = "Mobile number is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();

      if (job) {
        if (job.id) formDataToSend.append('job_id', job.id);
        if (job.job_code) formDataToSend.append('job_code', job.job_code);
        const jobTitle = job.position_title || job.public_job_title || job.title || '';
        if (jobTitle) formDataToSend.append('job_title', jobTitle);
      }

      formDataToSend.append('email', formData.email.trim());
      if (formData.firstname) formDataToSend.append('firstname', formData.firstname.trim());
      if (formData.lastname) formDataToSend.append('lastname', formData.lastname.trim());
      if (formData.mobile_number) formDataToSend.append('mobile_number', formData.mobile_number.trim());
      if (formData.country) formDataToSend.append('country', formData.country.trim());
      if (formData.state) formDataToSend.append('state', formData.state.trim());
      if (formData.city) formDataToSend.append('city', formData.city.trim());
      if (formData.address) formDataToSend.append('address', formData.address.trim());
      if (formData.totalexp) formDataToSend.append('totalexp', formData.totalexp.trim());
      if (formData.relavantexp) formDataToSend.append('relavantexp', formData.relavantexp.trim());

      Object.keys(formData.custom_fields).forEach(key => {
        if (formData.custom_fields[key]) {
          formDataToSend.append(`custom_fields.${key}`, formData.custom_fields[key]);
        }
      });

      formData.documents.forEach((file, index) => {
        formDataToSend.append(`document_fields.${index + 1}`, file);
      });

      const response = await fetch(`${API_BASE_URL}/api/cpal-apply`, {
        method: 'POST',
        body: formDataToSend
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || data.error?.message || 'Failed to submit application');
      }

      setSubmitSuccess(true);
      if (onSuccess) {
        onSuccess(data.data);
      }
      setTimeout(() => {
        if (onClose) {
          onClose();
        }
      }, 3000);

    } catch (error) {
      console.error('Application submission error:', error);
      setSubmitError(error.message || 'Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const jobTitle = job
    ? (job.position_title || job.public_job_title || job.title || 'Position')
    : 'Position';

  return (
    <div className="apply-form-overlay" onClick={onClose}>
      <div className="apply-form-container" onClick={(e) => e.stopPropagation()}>
        <button className="apply-form__close" onClick={onClose} aria-label="Close">
          ×
        </button>

        <div className="apply-form__header">
          <h2 className="apply-form__title">Apply for {jobTitle}</h2>
          <p className="apply-form__subtitle">Fill in your details to apply for this position</p>
        </div>

        {submitSuccess ? (
          <div className="apply-form__success">
            <div className="apply-form__success-icon">✓</div>
            <h3>Application Submitted Successfully!</h3>
            <p>Thank you for your interest. We will review your application and get back to you soon.</p>
            <p className="apply-form__success-note">This window will close automatically...</p>
          </div>
        ) : (
          <form className="apply-form" onSubmit={handleSubmit}>
            {submitError && (
              <div className="apply-form__error">
                <p>{submitError}</p>
              </div>
            )}

            <div className="apply-form__section">
              <h3 className="apply-form__section-title">Personal Information</h3>

              <div className="apply-form__row">
                <div className="apply-form__field">
                  <label htmlFor="firstname" className="apply-form__label">
                    First Name <span className="apply-form__required">*</span>
                  </label>
                  <input
                    type="text"
                    id="firstname"
                    name="firstname"
                    value={formData.firstname}
                    onChange={handleChange}
                    className={`apply-form__input ${errors.firstname ? 'apply-form__input--error' : ''}`}
                    placeholder="Enter your first name"
                  />
                  {errors.firstname && (
                    <span className="apply-form__error-text">{errors.firstname}</span>
                  )}
                </div>

                <div className="apply-form__field">
                  <label htmlFor="lastname" className="apply-form__label">Last Name</label>
                  <input
                    type="text"
                    id="lastname"
                    name="lastname"
                    value={formData.lastname}
                    onChange={handleChange}
                    className="apply-form__input"
                    placeholder="Enter your last name"
                  />
                </div>
              </div>

              <div className="apply-form__row">
                <div className="apply-form__field">
                  <label htmlFor="email" className="apply-form__label">
                    Email <span className="apply-form__required">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`apply-form__input ${errors.email ? 'apply-form__input--error' : ''}`}
                    placeholder="your.email@example.com"
                  />
                  {errors.email && (
                    <span className="apply-form__error-text">{errors.email}</span>
                  )}
                </div>

                <div className="apply-form__field">
                  <label htmlFor="mobile_number" className="apply-form__label">
                    Mobile Number <span className="apply-form__required">*</span>
                  </label>
                  <input
                    type="tel"
                    id="mobile_number"
                    name="mobile_number"
                    value={formData.mobile_number}
                    onChange={handleChange}
                    className={`apply-form__input ${errors.mobile_number ? 'apply-form__input--error' : ''}`}
                    placeholder="+1 234 567 8900"
                  />
                  {errors.mobile_number && (
                    <span className="apply-form__error-text">{errors.mobile_number}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="apply-form__section">
              <h3 className="apply-form__section-title">Location</h3>

              <div className="apply-form__row">
                <div className="apply-form__field">
                  <label htmlFor="country" className="apply-form__label">Country</label>
                  <input
                    type="text"
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className={`apply-form__input ${errors.country ? 'apply-form__input--error' : ''}`}
                    placeholder="Enter country"
                    disabled={loading}
                  />
                  {errors.country && (
                    <span className="apply-form__error-text">{errors.country}</span>
                  )}
                </div>

                <div className="apply-form__field">
                  <label htmlFor="state" className="apply-form__label">State</label>
                  <input
                    type="text"
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    className={`apply-form__input ${errors.state ? 'apply-form__input--error' : ''}`}
                    placeholder="Enter state"
                    disabled={loading}
                  />
                  {errors.state && (
                    <span className="apply-form__error-text">{errors.state}</span>
                  )}
                </div>

                <div className="apply-form__field">
                  <label htmlFor="city" className="apply-form__label">City</label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className={`apply-form__input ${errors.city ? 'apply-form__input--error' : ''}`}
                    placeholder="Enter city"
                    disabled={loading}
                  />
                  {errors.city && (
                    <span className="apply-form__error-text">{errors.city}</span>
                  )}
                </div>
              </div>

              <div className="apply-form__field">
                <label htmlFor="address" className="apply-form__label">Address</label>
                <textarea
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="apply-form__input apply-form__textarea"
                  placeholder="Enter your full address"
                  rows="3"
                />
              </div>
            </div>

            <div className="apply-form__section">
              <h3 className="apply-form__section-title">Additional Information</h3>

              <div className="apply-form__row">
                <div className="apply-form__field">
                  <label htmlFor="totalexp" className="apply-form__label">Total Experience</label>
                  <input
                    type="text"
                    id="totalexp"
                    name="totalexp"
                    value={formData.totalexp}
                    onChange={handleChange}
                    className="apply-form__input"
                    placeholder="Enter total experience"
                  />
                </div>

                <div className="apply-form__field">
                  <label htmlFor="relavantexp" className="apply-form__label">Relevant Experience</label>
                  <input
                    type="text"
                    id="relavantexp"
                    name="relavantexp"
                    value={formData.relavantexp}
                    onChange={handleChange}
                    className="apply-form__input"
                    placeholder="Enter relevant experience"
                  />
                </div>
              </div>
            </div>

            <div className="apply-form__section">
              <h3 className="apply-form__section-title">Documents</h3>

              <div className="apply-form__field">
                <label htmlFor="documents" className="apply-form__label">Upload Documents</label>
                <input
                  type="file"
                  id="documents"
                  name="documents"
                  onChange={handleFileChange}
                  className="apply-form__file-input"
                  multiple
                  accept=".pdf,.doc,.docx,.txt,.rtf,.jpg,.jpeg,.png,.gif"
                />
                <p className="apply-form__help-text">
                  Supported formats: PDF, DOC, DOCX, TXT, RTF, Images (Max 10MB per file)
                </p>
              </div>

              {formData.documents.length > 0 && (
                <div className="apply-form__documents-list">
                  {formData.documents.map((file, index) => (
                    <div key={index} className="apply-form__document-item">
                      <span className="apply-form__document-name">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => removeDocument(index)}
                        className="apply-form__document-remove"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="apply-form__actions">
              <button
                type="button"
                onClick={onClose}
                className="apply-form__btn apply-form__btn--cancel"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="apply-form__btn apply-form__btn--submit"
                disabled={loading}
              >
                {loading ? 'Submitting...' : 'Submit Application'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

